import { DestroyRef, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { FilterTableSettings, SnackbarV3Service, TableServiceV3 } from '@gea/digital-ui-lib';
import { catchError, debounceTime, Observable, of, switchMap, tap } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetApiControllerInsightPartnerService, AssetProductResponseDTO } from '@generated-apis/index';
import { showSnackbarError } from '@shared/utils/shared-helpers';
import { NETWORK_ERROR_MESSAGE, NETWORK_ERROR_TITLE } from '@shared/models/translations';
import {
  DASHBOARD_TABLE_ID,
  Machine,
  Organisations,
  possibleStatuses,
} from '../../../features/dashboard/components/machines-table/machines-table.constants';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly _loading = signal<boolean>(true);
  private readonly _entryCount = signal<number>(0);
  private readonly _machines = signal<Machine[]>([]);
  private readonly _organisations = signal<Organisations>(new Set([]));
  private readonly comparisonType = 'contains';

  // Expose signals to components
  readonly loading: Signal<boolean> = this._loading.asReadonly();
  readonly entryCount: Signal<number> = this._entryCount.asReadonly();
  readonly machines: Signal<Machine[]> = this._machines.asReadonly();
  readonly organisations: Signal<Organisations> = this._organisations.asReadonly();

  private readonly productsAPI = inject(AssetApiControllerInsightPartnerService);
  private readonly tableService = inject(TableServiceV3);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(SnackbarV3Service);

  constructor() {
    this.setupDataFlow();

    effect(() => {
      const machines = this._machines();
      const machinesWithValidStatuses = machines.map(
        (machine): Machine => ({
          ...machine,
          // @ts-ignore - null is not allowed from BE typing but sometimes null is returned and has to be handled
          status: possibleStatuses.includes(<Status>machine.status) ? machine.status : null,
        })
      );

      // Only update the machines signal one of the statuses is invalid
      if (!this.areEqual(machines, machinesWithValidStatuses)) this._machines.set(machinesWithValidStatuses);
    });
  }

  private setupDataFlow(): void {
    this.tableService
      .getFilterTableSettings(DASHBOARD_TABLE_ID)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(() => this._loading.set(true)),
        debounceTime(250),
        switchMap((settings): Observable<AssetProductResponseDTO> => {
          return this.fetchMachineData(settings);
        }),
        catchError((_): Observable<AssetProductResponseDTO> => {
          this._loading.set(false);
          this._entryCount.set(0);
          this._machines.set([]);
          // Return an empty ProductList on error
          return of({ entryCount: 0, pageEntries: [], organisations: new Set([]) });
        })
      )
      .subscribe({
        next: (productList: AssetProductResponseDTO) => {
          this.updateTableSignals(productList);
          this._loading.set(false);
        },
      });
  }

  private fetchMachineData(settings: FilterTableSettings | null): Observable<AssetProductResponseDTO> {
    let sortBy: string | undefined;
    let sortDir: string | undefined;
    let filters: Array<string> = [];

    Object.entries(settings?.columns ?? {}).forEach(([columnKey, columnSettings]) => {
      if (columnSettings.sort) {
        sortBy = columnKey;
        sortDir = columnSettings.sort;
      }

      if (columnSettings.filter && columnSettings.filter.length > 0) {
        columnSettings.filter.forEach((filterValue) => {
          filters.push(`${columnKey}[${this.comparisonType}]=${filterValue}`);
        });
      }
    });

    return this.productsAPI
      .getProducts(false, false, settings?.page, settings?.pageSize, settings?.searchValue, sortBy, sortDir, filters)
      .pipe(
        catchError((_): Observable<AssetProductResponseDTO> => {
          showSnackbarError(this.snackbarService, NETWORK_ERROR_TITLE, NETWORK_ERROR_MESSAGE);
          return of({ entryCount: 0, pageEntries: [], organisations: new Set([]) });
        })
      );
  }

  private updateTableSignals(productList: AssetProductResponseDTO): void {
    this._entryCount.set(productList.entryCount ?? 0);
    this._machines.set(productList.pageEntries);
    //TODO: Check if this can be improved in the future to avoid getting the organisations from the products endpoint
    if (!this._organisations().size) {
      this._organisations.set(new Set(productList.organisations));
    }
  }

  private areEqual(a: Machine[], b: Machine[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  public updateEntry(machineUUID: string, machineNickname: string | undefined) {
    const index = this._machines().findIndex((machine) => machine.geaPUID === machineUUID);
    if (index === -1) {
      return;
    }

    this._machines.update((machines) => {
      const updatedMachineList = [...machines];
      updatedMachineList[index] = { ...updatedMachineList[index], machineNickname };
      return updatedMachineList;
    });
  }
}
